import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Trash2, Save, Copy, Code2, GripVertical } from 'lucide-react';
import { Clip } from '../utils/api';
import { copyToClipboard } from '../utils/clipboard';
import { toast } from '../hooks/use-toast';
import { Draggable } from 'react-beautiful-dnd';

interface ClipCardProps {
  clip: Clip;
  pageId: string;
  index: number;
  onUpdate: (id: number, title: string, content: string) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
}

export function ClipCard({ clip, pageId, index, onUpdate, onDelete }: ClipCardProps) {
  const [title, setTitle] = useState(clip.title);
  const [content, setContent] = useState(clip.content);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCurlDialog, setShowCurlDialog] = useState(false);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
    setIsDirty(e.target.value !== clip.title || content !== clip.content);
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);
    setIsDirty(title !== clip.title || e.target.value !== clip.content);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(clip.id, title, content);
      setIsDirty(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    const success = await copyToClipboard(content);
    
    if (success) {
      toast({
        title: 'Copied to clipboard',
        description: `"${title}" content copied.`,
      });
    } else {
      toast({
        title: 'Error',
        description: 'Failed to copy to clipboard.',
        variant: 'destructive',
      });
    }
  };

  const contentSize = new Blob([content]).size;
  const isOverLimit = contentSize > 204800;

  const curlCommand = `curl https://pastey.link/cmd/${pageId}?clipId=${clip.id}`;

  return (
    <Draggable draggableId={clip.id.toString()} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={snapshot.isDragging ? 'opacity-50' : ''}
        >
          <Card className="mb-2">
            <CardHeader className="pb-2 pt-3 px-4">
              <div className="flex items-center gap-2">
                <div
                  {...provided.dragHandleProps}
                  className="cursor-move text-muted-foreground hover:text-foreground"
                >
                  <GripVertical className="h-4 w-4" />
                </div>
                <span className="text-xs text-muted-foreground">#{clip.id}</span>
                <Input
            value={title}
            onChange={handleTitleChange}
            placeholder="Clip title"
            className="flex-1 h-8"
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            title="Copy content"
            className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
          >
            <Copy className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowCurlDialog(true)}
            title="Show curl command"
            className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
          >
            <Code2 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(clip.id)}
            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 pt-0 px-4 pb-3">
        <Textarea
          value={content}
          onChange={handleContentChange}
          placeholder="Enter your content here..."
          className="min-h-[60px] max-h-[100px] resize-y font-mono text-sm"
        />
        <div className="flex items-center justify-between">
          <span className={`text-xs ${isOverLimit ? 'text-destructive' : 'text-muted-foreground'}`}>
            {(contentSize / 1024).toFixed(1)} KB / 200 KB
          </span>
          <Button
            onClick={handleSave}
            disabled={!isDirty || saving || isOverLimit}
            size="sm"
          >
            <Save className="h-4 w-4 mr-1" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
            </div>
          </CardContent>
        </Card>

    <Dialog open={showCurlDialog} onOpenChange={setShowCurlDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Curl Command</DialogTitle>
          <DialogDescription>
            Use this command to fetch this clip from the command line:
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-secondary p-3 rounded-md font-mono text-sm break-all">
            {curlCommand}
          </div>
          <Button
            onClick={() => {
              copyToClipboard(curlCommand);
              toast({
                title: 'Copied to clipboard',
                description: 'Curl command copied successfully.',
              });
            }}
            className="w-full"
          >
            <Copy className="h-4 w-4 mr-2" />
            Copy Command
          </Button>
          <div className="text-sm text-muted-foreground">
            You can also post new clips:
            <div className="bg-secondary p-2 rounded-md font-mono text-xs mt-2 break-all">
              {`curl -X POST https://pastey.link/cmd/${pageId} -H "Content-Type: application/json" -d '{"title": "New Clip", "content": "Your content"}'`}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
        </div>
      )}
    </Draggable>
  );
}